/* eslint-disable no-console */
/* eslint-disable import/prefer-default-export */
import { ref, computed } from 'vue';
import * as Msal from 'msal';
import { useTrace } from './useTrace';

const tracer = useTrace();

const baseScope = ['api://302098e9-1ec8-438d-b444-1f65d1352158/BrightFuture.Read'];

export const useAuth = () => {
  const config = {
    auth: {
      clientId: process.env.VUE_APP_AZURE_IDENTITY_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${process.env.VUE_APP_AZURE_IDENTITY_TENANT_ID}`,
      redirectUri: `${process.env.VUE_APP_APPURL}/auth/login`,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
    storeAuthStateInCookie: 'true',
  };

  const userAgentApplication = new Msal.UserAgentApplication(config);

  const isAuthenticated = ref(false);
  const user = ref(null);

  async function getScopedAccessToken(scopes) {
    const scopedAccessSpan = tracer.startSpan('getScopedAccessToken');
    const tokenRequest = {
      scopes,
    };

    const span = tracer.startSpan('acquireTokenSilent');
    span.spanContext().traceId = scopedAccessSpan?.spanContext()?.traceId;
    try {
      span.setAttribute('scopes', scopes);
      span.addEvent('acquireTokenSilent');
      const tokenResponse = await userAgentApplication.acquireTokenSilent(tokenRequest);
      if (tokenResponse) {
        window.localStorage.setItem('access_token', tokenResponse.accessToken);
        span.addEvent('acquireTokenSilent success');
        return tokenResponse.accessToken;
      }
    } catch (error) {
      span.setStatus({ code: 2, message: 'acquireTokenSilent failed' });
      span.addEvent('acquireTokenSilent failed');
      span.recordException(error);
      // acquireTokenSilent failed, fallback to acquireTokenRedirect
      const innerSpan = tracer.startSpan('acquireTokenRedirect');
      innerSpan.spanContext().traceId = scopedAccessSpan?.spanContext()?.traceId;
      try {
        innerSpan.addEvent('acquireTokenRedirect');
        tokenRequest.redirectUri = `${process.env.VUE_APP_APPURL}/auth/login`;
        const tokenResponseFallback = await userAgentApplication.acquireTokenRedirect(tokenRequest);
        window.localStorage.setItem('access_token', tokenResponseFallback.accessToken);
        innerSpan.addEvent('acquireTokenRedirect success');
        return tokenResponseFallback.accessToken;
      } catch (innerError) {
        innerSpan.recordException(innerError);
        innerSpan.setStatus({ code: 2, message: 'acquireTokenRedirect failed' });
        innerSpan.addEvent('acquireTokenRedirect failed');
      } finally {
        innerSpan.end();
      }
    } finally {
      span.end();
    }
    console.warn('no token found, returning nothing');
    scopedAccessSpan.setStatus({ code: 2, message: 'no token found' });
    scopedAccessSpan.end();
    return null;
  }

  async function getAccessToken() {
    const accessTokenSpan = tracer.startSpan('getAccessToken');
    const tokenRequest = {
      scopes: baseScope,
      forceRefresh: false,
    };
    accessTokenSpan.setAttribute('scopes', baseScope);
    accessTokenSpan.addEvent('acquireTokenSilent');
    try {
      const tokenResponse = await userAgentApplication.acquireTokenSilent(tokenRequest);
      if (tokenResponse) {
        accessTokenSpan.addEvent('acquireTokenSilent success');
        window.localStorage.setItem('access_token', tokenResponse.accessToken);
        accessTokenSpan.end();
        return tokenResponse.accessToken;
      }
    } catch (error) {
      accessTokenSpan.setStatus({ code: 2, message: 'acquireTokenSilent failed' });
      accessTokenSpan.addEvent('acquireTokenSilent failed');
      accessTokenSpan.recordException(error);
      // acquireTokenSilent failed, fallback to acquireTokenRedirect
      const innerSpan = tracer.startSpan('acquireTokenRedirect');
      try {
        innerSpan.spanContext().traceId = accessTokenSpan?.spanContext()?.traceId;
        innerSpan.addEvent('acquireTokenRedirect');
        tokenRequest.redirectUri = `${process.env.VUE_APP_APPURL}/auth/login`;
        const tokenResponseFallback = await userAgentApplication.acquireTokenRedirect(tokenRequest);
        innerSpan.addEvent('acquireTokenRedirect success');
        window.localStorage.setItem('access_token', tokenResponseFallback.accessToken);
        return tokenResponseFallback.accessToken;
      } catch (innerError) {
        innerSpan.recordException(innerError);
        innerSpan.setStatus({ code: 2, message: 'acquireTokenRedirect failed' });
        innerSpan.addEvent('acquireTokenRedirect failed');
      } finally {
        innerSpan.end();
      }
    } finally {
      accessTokenSpan.end();
    }
    console.warn('no token found, returning nothing');
    return null;
  }

  async function handleLoginRedirect() {
    console.debug('handle redirect');
    const account = await userAgentApplication.handleRedirectPromise();
    console.debug(account);
    if (account) {
      isAuthenticated.value = true;
      user.value = account;
      return true;
    }

    return false;
  }

  function login() {
    const span = tracer.startSpan('login');
    try {
      const loginRequest = {
        scopes: ['openid', 'profile', 'User.Read', 'offline_access', 'api://302098e9-1ec8-438d-b444-1f65d1352158/BrightFuture.Read Team.ReadBasic.All'],
        redirectUri: `${process.env.VUE_APP_APPURL}/auth/login`,
      };
      span.addEvent('loginRedirect');
      span.setAttribute('scopes', loginRequest.scopes);
      userAgentApplication.loginRedirect(loginRequest);
      isAuthenticated.value = true;
      user.value = userAgentApplication.getAccount();
      span.addEvent('loginRedirect success');
      span.setAttribute('user', user.value);
    } catch (error) {
      span.setStatus({ code: 2, message: 'loginRedirect failed' });
      span.addEvent('loginRedirect failed');
      span.recordException(error);
    } finally {
      span.end();
    }
  }

  async function logout() {
    const logoutSpan = tracer.startSpan('logout');
    logoutSpan.addEvent('logout');
    logoutSpan.setAttribute('user', user.value);
    userAgentApplication.logout();
    isAuthenticated.value = false;
    user.value = null;
    logoutSpan.addEvent('logout success');
    logoutSpan.end();
  }

  const accessToken = computed(async () => {
    if (userAgentApplication.getAccount()) {
      const span = tracer.startSpan('acquireTokenSilent');
      try {
        const response = await userAgentApplication.acquireTokenSilent({ scopes: config.scopes });
        span.setStatus({ code: 0, message: 'acquireTokenSilent success' });
        return response.accessToken;
      } catch (error) {
        span.setStatus({ code: 2, message: 'acquireTokenSilent failed' });
        span.recordException(error);

        const innerSpan = tracer.startSpan('acquireTokenPopup');
        try {
          const response = await userAgentApplication.acquireTokenPopup({ scopes: config.scopes });
          innerSpan.setStatus({ code: 0, message: 'acquireTokenPopup success' });
          return response.accessToken;
        } catch (innerError) {
          span.recordException(innerError);
          innerSpan.setStatus({ code: 2, message: 'acquireTokenPopup failed' });
        } finally {
          innerSpan.end();
        }
      } finally {
        span.end();
      }
    }
    return null;
  });

  return {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken,
    getScopedAccessToken,
    accessToken,
    userAgentApplication,
    handleLoginRedirect,
  };
};
